import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Mail, Plus, Edit, Trash2, Eye, Send, Code, FileText, Save, X, Copy } from "lucide-react";
import emailTemplateApi from "../../api/emailTemplateApi";
import { toast } from "react-toastify";

const EMAIL_TYPES = [
  { value: "MONTHLY_COMPLIANCE", label: "Monthly Compliance" },
  { value: "ONBOARDING", label: "Onboarding" },
  { value: "CONDITIONAL", label: "Conditional" },
  { value: "GREETING", label: "Greeting" },
  { value: "BILLING", label: "Billing" },
];

const RECIPIENT_TYPES = [
  { value: "PRIMARY_EMAIL", label: "Primary Email" },
  { value: "BILLING_EMAIL", label: "Billing Email" },
  { value: "CUSTOM", label: "Custom" },
];

const AVAILABLE_VARIABLES = [
  { key: "accountName", description: "Account name" },
  { key: "accountOwner", description: "Account owner name" },
  { key: "email", description: "Account email" },
  { key: "panNumber", description: "PAN number" },
  { key: "tanNumber", description: "TAN number" },
  { key: "mobileNumber", description: "Mobile number" },
  { key: "currentMonth", description: "Current month (e.g., January 2026)" },
  { key: "currentDate", description: "Current date (e.g., 06-Mar-2026)" },
  { key: "currentYear", description: "Current year" },
  { key: "fiscalYear", description: "Fiscal year (e.g., FY2025-26)" },
  { key: "documentType", description: "Document type" },
  { key: "period", description: "Period (month/quarter)" },
  { key: "status", description: "Document status" },
  { key: "filedDate", description: "Filed date" },
  { key: "dueDate", description: "Due date" },
  { key: "remarks", description: "Remarks" },
];

const EmailTemplateManager = () => {
  const token = useSelector((state) => state.auth.access_token);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [selectedType, setSelectedType] = useState("ALL");
  const [searchTerm, setSearchTerm] = useState("");
  const [showEditor, setShowEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [viewMode, setViewMode] = useState("list");

  const [formData, setFormData] = useState({
    templateName: "",
    emailType: "MONTHLY_COMPLIANCE",
    subject: "",
    bodyTemplate: "",
    htmlTemplate: "",
    isHtml: true,
    particulars: "",
    recipientType: "PRIMARY_EMAIL",
    isActive: true,
  });

  useEffect(() => {
    if (token) {
      fetchTemplates();
    }
  }, [token]);

  useEffect(() => {
    filterTemplates();
  }, [templates, selectedType, searchTerm]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await emailTemplateApi.getAllTemplates(token);
      if (response && Array.isArray(response)) {
        setTemplates(response);
      } else if (response && response.data) {
        setTemplates(response.data);
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
      toast.error("Failed to load email templates");
    } finally {
      setLoading(false);
    }
  };

  const filterTemplates = () => {
    let filtered = templates;

    if (selectedType !== "ALL") {
      filtered = filtered.filter((t) => t.emailType === selectedType);
    }

    if (searchTerm) {
      filtered = filtered.filter(
        (t) =>
          t.templateName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          t.subject.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredTemplates(filtered);
  };

  const handleCreate = () => {
    setEditingTemplate(null);
    setFormData({
      templateName: "",
      emailType: "MONTHLY_COMPLIANCE",
      subject: "",
      bodyTemplate: "",
      htmlTemplate: getDefaultHtmlTemplate(),
      isHtml: true,
      particulars: "",
      recipientType: "PRIMARY_EMAIL",
      isActive: true,
    });
    setShowEditor(true);
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      templateName: template.templateName,
      emailType: template.emailType,
      subject: template.subject,
      bodyTemplate: template.bodyTemplate,
      htmlTemplate: template.htmlTemplate || getDefaultHtmlTemplate(),
      isHtml: template.isHtml,
      particulars: template.particulars || "",
      recipientType: template.recipientType,
      isActive: template.isActive,
    });
    setShowEditor(true);
  };

  const handleSave = async () => {
    if (!formData.templateName || !formData.subject) {
      toast.error("Template name and subject are required");
      return;
    }

    try {
      if (editingTemplate) {
        await emailTemplateApi.updateTemplate(editingTemplate.id, formData, token);
        toast.success("Template updated successfully");
      } else {
        await emailTemplateApi.createTemplate(formData, token);
        toast.success("Template created successfully");
      }
      setShowEditor(false);
      fetchTemplates();
    } catch (error) {
      console.error("Error saving template:", error);
      toast.error("Failed to save template");
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm("Are you sure you want to delete this template?")) {
      return;
    }

    try {
      await emailTemplateApi.deleteTemplate(templateId, token);
      toast.success("Template deleted successfully");
      fetchTemplates();
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error("Failed to delete template");
    }
  };

  const handleToggleStatus = async (templateId) => {
    try {
      await emailTemplateApi.toggleTemplateStatus(templateId, token);
      toast.success("Template status updated");
      fetchTemplates();
    } catch (error) {
      console.error("Error toggling status:", error);
      toast.error("Failed to update template status");
    }
  };

  const insertVariable = (variable) => {
    const placeholder = `{{${variable}}}`;
    if (formData.isHtml) {
      setFormData({ ...formData, htmlTemplate: formData.htmlTemplate + placeholder });
    } else {
      setFormData({ ...formData, bodyTemplate: formData.bodyTemplate + placeholder });
    }
  };

  const getDefaultHtmlTemplate = () => {
    return `<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="margin: 0; padding: 0; font-family: Arial, sans-serif; background-color: #f4f4f4;">
    <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f4f4f4; padding: 20px;">
        <tr>
            <td align="center">
                <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
                    <!-- Header -->
                    <tr>
                        <td style="background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%); padding: 30px; text-align: center;">
                            <h1 style="margin: 0; color: #ffffff; font-size: 28px;">Email Title</h1>
                        </td>
                    </tr>
                    
                    <!-- Content -->
                    <tr>
                        <td style="padding: 30px;">
                            <p style="margin: 0 0 20px; color: #374151; font-size: 16px;">Dear <strong>{{accountName}}</strong>,</p>
                            
                            <p style="margin: 0 0 20px; color: #374151; font-size: 16px;">
                                Your email content goes here.
                            </p>
                            
                            <p style="margin: 20px 0 0; color: #6b7280; font-size: 14px;">
                                Best regards,<br>
                                Your Team
                            </p>
                        </td>
                    </tr>
                    
                    <!-- Footer -->
                    <tr>
                        <td style="background-color: #f9fafb; padding: 20px; text-align: center; border-top: 1px solid #e5e7eb;">
                            <p style="margin: 0; color: #9ca3af; font-size: 11px;">This is an automated notification.</p>
                        </td>
                    </tr>
                </table>
            </td>
        </tr>
    </table>
</body>
</html>`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Email Template Manager</h1>
          <p className="text-slate-600 mt-1">Create and manage email templates with HTML support</p>
        </div>
        <button
          onClick={handleCreate}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
        >
          <Plus size={20} />
          Create Template
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Search</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search templates..."
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Email Type</label>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="ALL">All Types</option>
              {EMAIL_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">View Mode</label>
            <div className="flex gap-2">
              <button
                onClick={() => setViewMode("list")}
                className={`flex-1 px-3 py-2 rounded-lg transition-colors ${
                  viewMode === "list"
                    ? "bg-indigo-100 text-indigo-700 font-medium"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                <FileText className="inline mr-2" size={16} />
                List
              </button>
              <button
                onClick={() => setViewMode("grid")}
                className={`flex-1 px-3 py-2 rounded-lg transition-colors ${
                  viewMode === "grid"
                    ? "bg-indigo-100 text-indigo-700 font-medium"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                <Code className="inline mr-2" size={16} />
                Grid
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Templates List */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Template Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Subject
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Format
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredTemplates.map((template) => (
                <tr key={template.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Mail className="w-5 h-5 text-slate-400 mr-3" />
                      <div>
                        <div className="text-sm font-medium text-slate-900">
                          {template.templateName}
                        </div>
                        {template.isSystemTemplate && (
                          <span className="text-xs text-slate-500">System Template</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                      {template.emailType}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-slate-900 max-w-md truncate">
                      {template.subject}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        template.isHtml
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {template.isHtml ? "HTML" : "Plain Text"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleToggleStatus(template.id)}
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        template.isActive
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {template.isActive ? "Active" : "Inactive"}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleEdit(template)}
                        className="text-indigo-600 hover:text-indigo-900"
                        title="Edit"
                      >
                        <Edit size={16} />
                      </button>
                      {!template.isSystemTemplate && (
                        <button
                          onClick={() => handleDelete(template.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredTemplates.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <Mail className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>No templates found</p>
            </div>
          )}
        </div>
      </div>

      {/* Editor Modal */}
      {showEditor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-slate-800">
                {editingTemplate ? "Edit Template" : "Create Template"}
              </h2>
              <button
                onClick={() => setShowEditor(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X size={24} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Form Fields */}
                <div className="lg:col-span-2 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Template Name *
                    </label>
                    <input
                      type="text"
                      value={formData.templateName}
                      onChange={(e) =>
                        setFormData({ ...formData, templateName: e.target.value })
                      }
                      placeholder="e.g., CUSTOM_WELCOME_EMAIL"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Email Type *
                      </label>
                      <select
                        value={formData.emailType}
                        onChange={(e) =>
                          setFormData({ ...formData, emailType: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        {EMAIL_TYPES.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Recipient Type
                      </label>
                      <select
                        value={formData.recipientType}
                        onChange={(e) =>
                          setFormData({ ...formData, recipientType: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        {RECIPIENT_TYPES.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Subject *
                    </label>
                    <input
                      type="text"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      placeholder="e.g., Welcome to {{accountName}}"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="flex items-center gap-2 mb-2">
                      <input
                        type="checkbox"
                        checked={formData.isHtml}
                        onChange={(e) =>
                          setFormData({ ...formData, isHtml: e.target.checked })
                        }
                        className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                      />
                      <span className="text-sm font-medium text-slate-700">Use HTML Template</span>
                    </label>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {formData.isHtml ? "HTML Template" : "Plain Text Template"}
                    </label>
                    <textarea
                      value={formData.isHtml ? formData.htmlTemplate : formData.bodyTemplate}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          [formData.isHtml ? "htmlTemplate" : "bodyTemplate"]: e.target.value,
                        })
                      }
                      rows={15}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm"
                      placeholder={
                        formData.isHtml
                          ? "Enter HTML template..."
                          : "Enter plain text template..."
                      }
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={formData.particulars}
                      onChange={(e) =>
                        setFormData({ ...formData, particulars: e.target.value })
                      }
                      rows={2}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Brief description of this template..."
                    />
                  </div>
                </div>

                {/* Variables Panel */}
                <div className="space-y-4">
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <h3 className="text-sm font-semibold text-slate-800 mb-3">
                      Available Variables
                    </h3>
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {AVAILABLE_VARIABLES.map((variable) => (
                        <button
                          key={variable.key}
                          onClick={() => insertVariable(variable.key)}
                          className="w-full text-left px-3 py-2 bg-white border border-slate-200 rounded hover:bg-indigo-50 hover:border-indigo-300 transition-colors group"
                        >
                          <div className="flex items-center justify-between">
                            <code className="text-xs font-mono text-indigo-600">
                              {`{{${variable.key}}}`}
                            </code>
                            <Copy
                              size={14}
                              className="text-slate-400 group-hover:text-indigo-600"
                            />
                          </div>
                          <div className="text-xs text-slate-500 mt-1">
                            {variable.description}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-blue-900 mb-2">💡 Tips</h4>
                    <ul className="text-xs text-blue-800 space-y-1">
                      <li>• Click variables to insert them</li>
                      <li>• Use HTML for rich formatting</li>
                      <li>• Test with preview before saving</li>
                      <li>• System templates cannot be deleted</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-3">
              <button
                onClick={() => setShowEditor(false)}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
              >
                <Save size={16} />
                {editingTemplate ? "Update Template" : "Create Template"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailTemplateManager;
